import { API } from '@/models/core'
import { FantasyUserTeam } from '@/models/fantasy'
import { FantasyRuleManager } from './rule/manager'

export class FantasyPlayerSelectionState {
  constructor (player) {
    this.key = player.player.key
    this.player = player
    this.canSelect = { pass: true }
    this.reason = null
    this.boostName = null
    this.boostIndex = -1
    this.selected = -1
  }
}

export class FantasyTeamSelectionState {
  constructor (rule) {
    this.rule = rule
    this.ruleManager = new FantasyRuleManager(rule)
    this.fullfillResult = null
    this.playersState = {}
    this.creditsUsed = []
    this.playerKeys = []
    this.boostPlayers = {}
    this.creditsUsedTotal = 0
    this.teamPlayersCount = {}
    this.matchPlayersCount = {}
    this.rolesCount = {}
  }

  resetCounts () {
    this.fullfillResult = null
    this.creditsUsed = []
    this.creditsUsedTotal = 0
    this.teamPlayersCount = {}
    this.matchPlayersCount = {}
    this.rolesCount = {}
  }

  initPlayer (player) {
    const selectionState = new FantasyPlayerSelectionState(player)
    this.playersState[selectionState.key] = selectionState
  }

  selectedBoostPlayers () {
    let boostKeys = Array(this.rule.boost_names.length).fill(null)
    Object.keys(this.playersState).map((key) => {
      if (this.playersState[key].boostIndex > -1) {
        const index = this.playersState[key].boostIndex
        boostKeys[index] = key
      }
    })

    return boostKeys
  }

  updatePlayersState () {
    this.resetCounts()
    const creditsBoost = (this.rule.credits_boost) ? this.rule.credits_boost : []

    this.playerKeys.map((key) => {
      const playerState = this.playersState[key]
      const player = playerState.player
      const playerRole = player.primary_role
      let credits = player.credits

      if (playerState.boostIndex > -1 && creditsBoost.length > playerState.boostIndex) {
        credits *= creditsBoost[playerState.boostIndex]
      }

      this.creditsUsed.push(credits)
      this.creditsUsedTotal += credits

      if (!this.teamPlayersCount[player.team.key]) {
        this.teamPlayersCount[player.team.key] = 0
      }
      this.teamPlayersCount[player.team.key] += 1

      if (!this.rolesCount[playerRole]) {
        this.rolesCount[playerRole] = 0
      }
      this.rolesCount[playerRole] += 1

      player.all_matches.forEach((matchKey) => {
        if (!this.matchPlayersCount[matchKey]) {
          this.matchPlayersCount[matchKey] = 0
        }
        this.matchPlayersCount[matchKey] += 1
      })
    })

    Object.keys(this.playersState).map((key) => {
      const result = this.ruleManager.canSelectPlayer(key, this)
      const playerState = this.playersState[key]
      playerState.canSelect = result
    })

    this.fullfillResult = this.ruleManager.validate(this)
  }

  addPlayer (playerKey) {
    this.playersState[playerKey].selected = this.playerKeys.length
    this.playerKeys.push(playerKey)
    this.updatePlayersState()
  }

  removePlayer (playerKey) {
    const index = this.playersState[playerKey].selected
    if (index > -1) {
      this.playerKeys.splice(index, 1)

      for (let i = index; i < this.playerKeys.length; i++) {
        const key = this.playerKeys[i]
        this.playersState[key].selected += -1
      }

      this.playersState[playerKey].selected = -1
      this.removePlayerBoost(playerKey)
      this.updatePlayersState()
    }
  }

  addBoostPlayer (index, playerKey) {
    const name = this.rule.boost_names[index]
    this.removeBoostByIndex(index)

    this.playersState[playerKey].boostIndex = index
    this.playersState[playerKey].boostName = name
    this.updatePlayersState()
  }

  removeBoostByIndex (index) {
    let found = null
    Object.keys(this.playersState).map((key) => {
      if (this.playersState[key].boostIndex === index) {
        found = key
        this.playersState[key].boostIndex = -1
        this.playersState[key].boostName = null
      }
    })
    return found
  }

  removePlayerBoost (playerKey) {
    const index = this.playersState[playerKey].boostIndex
    if (index > -1) {
      this.removeBoostByIndex(index)
      this.updatePlayersState()
    }
  }
}

export class FantasyTeamSelector {
  constructor (rule, game, credits, userTeam) {
    this.rule = rule
    this.game = game
    this.credits = credits
    this.userTeam = userTeam || null

    // this.playerKeys = []
    this.teamState = new FantasyTeamSelectionState(this.rule)

    this._ts = new Date().getTime()

    credits.player_keys.forEach((key) => {
      this.initPlayer(key)
    })

    if (userTeam) {
      this.updateUserTeam(userTeam)
    }

    this.updatePlayersState()
  }

  updateUserTeam (userTeam, clone) {
    if (!clone) {
      this.userTeam = userTeam
    }

    userTeam.team.players.map((key, i) => {
      this.removePlayer(key)
      this.addPlayer(key)
    })

    userTeam.team.boost_players.map((key, i) => {
      this.addBoostPlayer(i, key)
    })
  }

  initPlayer (key) {
    this.teamState.initPlayer(this.credits.players[key])
  }

  selectable (playerKey) {
    const playerState = this.teamState.playersState[playerKey]
    if (!playerState) {
      return false
    }

    return playerState.canSelect.pass
  }

  isSelected (playerKey) {
    const playerState = this.teamState.playersState[playerKey]
    if (!playerState) {
      return false
    }

    return playerState.selected > -1
  }

  selectableReason (playerKey) {
    const playerState = this.teamState.playersState[playerKey]
    if (!playerState) {
      return `${playerKey} player are not allowed for this match`
    }

    return playerState.canSelect.msg
  }

  canSelectPlayer (playerKey) {
    const playerState = this.teamState.playersState[playerKey]
    return playerState.canSelect
  }

  validateTeam () {
    return this.teamState.ruleManager.validate()
  }

  updatePlayersState () {
    this.teamState.updatePlayersState()
  }

  addPlayer (playerKey) {
    this.teamState.addPlayer(playerKey)
  }

  removePlayer (playerKey) {
    this.teamState.removePlayer(playerKey)
  }

  addBoostPlayer (index, playerKey) {
    this.teamState.addBoostPlayer(index, playerKey)
  }

  removeBoostByIndex (index) {
    this.teamState.removeBoostByIndex(index)
  }

  removePlayerBoost (playerKey) {
    this.teamState.removePlayerBoost(playerKey)
  }

  createTeam () {
    let boostKeys = this.teamState.selectedBoostPlayers()
    const postData = {
      key: (this.userTeam) ? this.userTeam.key : null,
      players: this.teamState.playerKeys,
      boostPlayers: boostKeys
    }

    const params = {}
    return API.instance().post(`/fantasy-game/${API.faKey}/game/${this.game.key}/team/`, postData, { params }).then((resp) => {
      return resp
    })
  }

  save () {
    let boostKeys = this.teamState.selectedBoostPlayers()
    const postData = {
      key: (this.userTeam) ? this.userTeam.key : null,
      players: this.teamState.playerKeys,
      boostPlayers: boostKeys
    }

    const params = {}
    return API.instance().post(`/fantasy-game/${API.faKey}/game/${this.game.key}/participate/`, postData, { params }).then((resp) => {
      let teamRaw = resp.data.data
      const userTeam = FantasyUserTeam.createFromJson(teamRaw)
      this.updateUserTeam(userTeam)
      return userTeam
    })
  }

  teamParticipation (team) {
    const postData = {
      key: team.key
    }

    const params = {}
    return API.instance().post(`/fantasy-game/${API.faKey}/game/${this.game.key}/user-participation/`, postData, { params }).then((resp) => {
      let teamRaw = resp.data.data
      const userTeam = FantasyUserTeam.createFromJson(teamRaw)
      this.updateUserTeam(userTeam)
      return userTeam
    })
  }

  removeParticipation (key) {
    const params = {}
    return API.instance().delete(`/fantasy-game/${API.faKey}/game/${this.game.key}/user-participation/?key=${key}`, { params }).then((resp) => {
      return resp.data.data
    })
  }

  deleteTeam (key) {
    return API.instance().delete(`/fantasy-game/${API.faKey}/game/${this.game.key}/participate/?key=${key}`).then((resp) => {
      console.log('team deleted', key)
      return resp.data.data
    })
  }
}
